/// Private revenue / funnel dashboard. Reached only via `?dashboard`
/// and gated to admin emails (both here for UX and, authoritatively, in
/// the `getDashboardMetrics` Cloud Function). Tracks the standing goal:
/// $750 of net proceeds from 2026-11-01 to 2027-01-31 (QuarterGoalCard, first
/// on the page). See docs/2026-06-14-revenue-dashboard.md.
import { Fragment, useCallback, useEffect, useState } from 'react'
import { httpsCallable } from '../firebase/callable'
import { firebaseFunctions } from '../firebase/app'
import { AnnouncementComposer } from './AnnouncementComposer'
import { DiscoveryCard, type DiscoveryLive } from './DiscoveryCard'
import { QuarterGoalCard, type QuarterGoalLive } from './QuarterGoalCard'
import {
  BUYER_FILTER_KEYS,
  BUYER_FILTER_LABELS,
  countBuyerFilters,
  filterBuyers,
  formatMinutesShort,
  sortBuyersNewestFirst,
  summarizeBuyerRow,
  type BuyerFilterKey,
} from '../util/buyerLedger'

interface Pending {
  status: 'pending'
  note: string
}
interface Bucket {
  count: number
  pct: number
}
type PostLevelAction =
  | 'shown' | 'continue' | 'try_again' | 'play_again'
  | 'hand_over' | 'back_to_tower' | 'share' | 'rate'
interface PostLevelActionRow {
  audience: string
  isAcademy: boolean
  action: PostLevelAction
  count: number
  pctOfShown: number | null
}
interface PostLevelActionsLive {
  source: 'live'
  rows: PostLevelActionRow[]
  note: string
  window: number
}
// Next Shift: D1 by experiment arm + the treatment's own funnel. Mirrors
// nextShiftD1Core.ts.
type NextShiftVariant = 'control' | 'treatment'
interface NextShiftArm {
  variant: NextShiftVariant
  cohort: number
  retained: number
  pct: number | null
  shown: number
  /// Added with the version split (2026-09-23); optional until the function
  /// that sends it is deployed, so the page never breaks on an older payload.
  tapped?: number
  scheduled: number
  opened: number
}
interface NextShiftVersionArms {
  version: string
  arms: NextShiftArm[]
}
interface NextShiftD1Live {
  source: 'live'
  arms: NextShiftArm[]
  byVersion?: NextShiftVersionArms[]
  note: string
}
interface FunnelLive {
  source: 'live'
  installs: number
  onboarded: number
  viewedPurchase: number
  purchasers: number
  onboardedPct: number
  viewedPurchasePct: number
  conversionPct: number
  // Rolling window (2026-10-24): the counts above are the last windowDays days' installs.
  windowDays?: number
  sinceStart?: { since: string; installs: number; purchasers: number; conversionPct: number }
  note: string
}
// The insight paragraph — deterministic "look here, not there" read of the
// funnel's own this-week-vs-last-week numbers (mirrors funnelInsightCore.ts).
interface FunnelInsightLive {
  source: 'live'
  asOf: string
  cached: boolean
  paragraph: string
  engine: 'deterministic-v1'
  firedDetectors: string[]
  note: string
}
interface TimeToConvertLive {
  source: 'live'
  converted: number
  avgHours: number | null
  medianHours: number | null
  note: string
}
interface UsersByDayLive {
  source: 'live'
  // Distinct active users per day (GA4 DAU), trailing 30 complete days.
  series: { date: string; users: number }[]
  note: string
}
// --- Academy funnel (live depth→engagement cut) ---
interface RetentionLive {
  source: 'live'
  d1: { cohort: number; retained: number; pct: number }
  // #126/#128: D7 and the day-7 conversion endpoint; null while cohorts mature.
  d7?: { cohort: number; retained: number; pct: number } | null
  conversion?: { cohort: number; byDay1Pct: number; byDay7Pct: number } | null
  // Rolling window (2026-10-24): the figures above are the last windowDays days' installs.
  windowDays?: number
  sinceStart?: { since: string; d1: { cohort: number; retained: number; pct: number } } | null
  note: string
}
interface RevenueVsSpendLive {
  source: 'live' | 'partial'
  windowDays: number
  daysWithData: number
  proceedsUSD30d: number
  paidUnits30d: number
  downloads30d: number
  dailyAvgProceedsUSD: number
  spendUSD30d: number | null
  spendDays: number
  contributionMarginUSD30d: number | null
  note: string
  // Real-time, all-storefronts, FX-converted companion to proceedsUSD30d
  // above (which is US-storefront-only — see allStorefrontsNote). Null when
  // the purchases webhook has no rows in the window.
  allStorefrontsGrossUSD30d?: number | null
  allStorefrontsNote?: string
  // Every storefront's ASC proceeds, FX-converted, and the margin after ASA
  // spend: what the money card shows.
  allStorefrontsProceedsUSD30d?: number | null
  allStorefrontsContributionMarginUSD30d?: number | null
}

// Buyer cohort — honest FGU/BGU/season split of the base-owner count (mirrors
// functions/src/buyerCohortCore.ts).
interface BuyerCohortLive {
  source: 'live'
  baseOwners: number
  fguOwners: number
  fguOwnersInclLegacy?: number
  bguOwners: number
  dualOwners: number
  unattributedOwners: number
  legacyStageOwners: number
  seasonBuyers: number
  bySeasonBuyers: { season: number; buyers: number }[]
  seasonAttachPct: number | null
  note: string
}

// Revenue by product family — two sources, same shape (mirrors
// functions/src/revenueByProductCore.ts's ProductRevenueSummary).
interface ProductRevenueSummary {
  family: 'fgu' | 'bgu' | 'season' | 'legacy_stage' | 'unknown'
  seasonNumber: number | null
  units: number
  grossUSD: number
  proceedsUSD: number
  unconvertedCurrencies: string[]
}
interface RevenueByProductWebhookLive {
  source: 'live'
  families: ProductRevenueSummary[]
  note: string
}
interface RevenueByProduct {
  webhook: RevenueByProductWebhookLive | Pending
}
interface HistoryPoint {
  date: string
  proceedsUSD30d?: number | null
  spendUSD30d?: number | null
  marginUSD30d?: number | null
  // All storefronts, FX-converted: a series from 2026-09-25.
  proceedsAllStorefrontsUSD30d?: number | null
  marginAllStorefrontsUSD30d?: number | null
  active30d?: number | null
  funnelInstalls?: number | null
  conversionPct?: number | null
  d1Pct?: number | null
  engagedGe5Pct?: number | null
  engagedGe25Pct?: number | null
}
interface FencePost {
  date: string
  type: string
  label: string
}
function isPending<T extends { source: string }>(x: T | Pending): x is Pending {
  return (x as Pending).status === 'pending'
}
interface Crashes {
  available: boolean
  windowDays: number
  issues: number
  events: number
  fatalIssues: number
  fatalEvents: number
  topIssues: { title: string; subtitle: string; events: number; fatal: boolean }[]
  note?: string
  reason?: string
}
interface DeepLinks {
  source: 'live'
  ok: boolean
  checkedAt: string | null
  targets: { id: string; label: string; ok: boolean; detail: string }[]
}

type LevelMode = 'air' | 'ground' | 'academy' | 'tutorial' | 'live' | 'sar' | 'oceanic'
interface LevelScoreVersionSlice {
  version: string
  starts: number
  finishPct: number | null
  winPct: number | null
  avgStar: number | null
  ratings: number
  outcomeFinishPct: number | null
  abandonRate: number | null
  hasOutcomeData: boolean
}
interface LevelScoreRow {
  levelId: string
  mode: LevelMode
  starts: number
  players: number
  ends: number
  wins: number
  finishPct: number | null
  winPct: number | null
  ratings: number
  avgStar: number | null
  /// #101: display star = avgStar shrunk toward the cross-level prior
  /// (C=10); optional so a cached pre-upgrade block renders unchanged.
  bayesStar?: number | null
  top2BoxPct?: number | null
  lowSharePct?: number | null
  outcomeFinishPct: number | null
  abandonRate: number | null
  hasOutcomeData: boolean
  devLowSignal: boolean
  concern: boolean
  byVersion: LevelScoreVersionSlice[]
}
interface LevelVersionSplit {
  lo: string
  hi: string
}
interface LevelScorecardLive {
  source: 'live'
  windowDays: number
  asOf: string
  cached: boolean
  note: string
  levels: LevelScoreRow[]
  concernCount: number
  versionSplit: LevelVersionSplit | null
}
// --- Buyer Ledger (mirrors functions/src/buyerProfileCore.ts; duplicated
// across the functions/web boundary by convention, same as LevelScorecard's
// types above). Served by the SEPARATE getBuyerProfiles callable, not
// embedded in Metrics — see DashboardScreen's load() for why. ---
interface BuyerAnchor {
  txn: string
  productId: string | null
  productIdShort: string | null
  price: number | null
  currency: string | null
  purchaseDate: string | null
  environment: string | null
}
interface BuyerIdentity {
  uid: string | null
  installDate: string | null
  installPlace: string | null
  purchasePlace: string | null
}
interface WallTouchSummary {
  count: number
  reasons: string[]
  minutesFromLastTouchToPurchase: number | null
}
interface DoorViewSurfaceCount {
  surface: string
  count: number
}
interface DoorViewSummary {
  bgu: DoorViewSurfaceCount[]
  season: DoorViewSurfaceCount[]
}
interface FiamCampaignCount {
  campaign: string
  impressions: number
  actions: number
}
interface FiamSummary {
  impressions: number
  actions: number
  byCampaign: FiamCampaignCount[]
}
interface ExperimentArm {
  experiment: string
  variant: string
}
interface NudgeTrailEntry {
  event: string
  params: Record<string, string | number | null>
  minutesBeforePurchase: number
}
interface JourneyBefore {
  minutesToPurchase: number | null
  sessions: number | null
  activeDays: number | null
  levelStarts: number | null
  distinctLevels: number | null
  levelEnds: number | null
  academyLessons: number | null
  academyCompleted: boolean | null
  minutesInAcademy: number | null
  episodeStarts: number | null
  episodeCompletes: number | null
  livePlays: number | null
  wallTouches: WallTouchSummary | null
  doorViews: DoorViewSummary | null
  fiam: FiamSummary | null
  experimentArms: ExperimentArm[]
  nudgeTrail: NudgeTrailEntry[]
  purchaseHourLocal: number | null
  weekday: string | null
  timeBasis: 'purchase-place' | 'utc'
  lastSelectedDifficulty: string | null
  levelsWithProgress: number | null
}
interface JourneyAfter {
  playsAfterD1: number | null
  playsAfterD7: number | null
  lastSeenDaysAfter: number | null
}
type RefundOutcome = 'none' | 'requested' | 'refunded'
interface RefundBlock {
  consumptionRequestReason: string | null
  consumptionRequestCount: number
  refundedAt: string | null
  outcome: RefundOutcome
  riskFlags: string[]
}
interface DataHorizon {
  earliestBqDate: string | null
  trailComplete: boolean
}
interface BuyerProfile {
  anchor: BuyerAnchor
  identity: BuyerIdentity
  journey: { before: JourneyBefore; after: JourneyAfter }
  refund: RefundBlock
  dataHorizon: DataHorizon
  narrative: string
  engine: 'deterministic-v1'
  source: string
}
interface BuyerLedgerTotals {
  buyers: number
  countries: number
  medianMinutesToPurchase: number | null
  wallTouchPct: number | null
  refunds: number
}
interface BuyerLedgerResponse {
  generatedAt: string
  windowNote: string
  totals: BuyerLedgerTotals
  profiles: BuyerProfile[]
}
type BuyerState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: BuyerLedgerResponse }

interface Metrics {
  generatedAt: string
  metricsStartDate: string
  activeWindowDays: number
  crashes: Crashes
  deeplinks: DeepLinks | Pending
  // The quarter goal (functions/src/quarterGoalCore.ts); optional until the
  // function that sends it is deployed.
  quarterGoal?: QuarterGoalLive | Pending
  install: { source: 'live'; totalUsers: number; active30d: number; note: string }
  engagement: {
    source: 'live'
    total: number
    buckets: Record<'ge2' | 'ge3' | 'ge5' | 'ge10' | 'ge25', Bucket>
    note: string
  }
  conversion: {
    source: 'live'
    buyers: number
    ratePctOfAllUsers: number
    note: string
  }
  buyerCohort?: BuyerCohortLive
  revenueByProduct?: RevenueByProduct
  funnelInsight: FunnelInsightLive | Pending
  funnel: FunnelLive | Pending
  usersByDay: UsersByDayLive | Pending
  postLevelActions?: PostLevelActionsLive | Pending
  nextShiftD1?: NextShiftD1Live | Pending
  levelScorecard: LevelScorecardLive | Pending
  discovery?: DiscoveryLive | Pending
  retention: RetentionLive | Pending
  timeToConvert: TimeToConvertLive | Pending
  revenueVsSpend: RevenueVsSpendLive | Pending
  history: HistoryPoint[]
  fencePosts?: FencePost[]
}

const callMetrics = httpsCallable<unknown, Metrics>(firebaseFunctions, 'getDashboardMetrics')
// Buyer Ledger — a SEPARATE callable from getDashboardMetrics (its own
// admin-enforcement check server-side, same pattern), so a slow/failed
// buyer-profile read never blanks the rest of the dashboard or vice versa.
const callBuyerProfiles = httpsCallable<unknown, BuyerLedgerResponse>(firebaseFunctions, 'getBuyerProfiles')

function hoursAgo(iso: string | null): string {
  if (!iso) return 'never'
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000
  if (h < 1) return 'under 1h ago'
  return `${Math.round(h)}h ago`
}

function DeepLinksSection({ data }: { data: DeepLinks }) {
  return (
    <Section title="Deep links" note={`Last checked ${hoursAgo(data.checkedAt)}`}>
      <div className="space-y-1">
        {data.targets.map((t) => (
          <div key={t.id} className="flex items-center gap-2 text-sm">
            <span className={t.ok ? 'text-emerald-400' : 'text-red-400'}>{t.ok ? '✓' : '✗'}</span>
            <span className="text-slate-200">{t.label}</span>
            {!t.ok && <span className="text-red-300 tabular-nums">— {t.detail}</span>}
          </div>
        ))}
      </div>
    </Section>
  )
}

// Lifetime, all-storefronts season revenue — summed from the webhook-sourced
// per-family breakdown (populates immediately, not gated on ASC's report
// lag). Null while that block is still 'pending' (no purchases yet, or the
// backend hasn't deployed this field).
function seasonRevenueUsd(revenueByProduct: RevenueByProduct | undefined): number | null {
  const webhook = revenueByProduct?.webhook
  if (!webhook || isPending(webhook)) return null
  return webhook.families.filter((f) => f.family === 'season').reduce((sum, f) => sum + f.grossUSD, 0)
}

// Base owners split (FGU vs BGU vs dual vs unattributed) + season buyers,
// attach %, and lifetime season revenue. Replaces the old "FGU buyers" tile's
// implicit claim that count was FGU-only — see buyerCohortCore.ts.
function BuyerCohortSection({ cohort, revenueByProduct }: { cohort: BuyerCohortLive; revenueByProduct?: RevenueByProduct }) {
  const seasonRev = seasonRevenueUsd(revenueByProduct)
  return (
    <Section title="Base owners and seasons" note={cohort.note}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="FGU / BGU" value={`${cohort.fguOwnersInclLegacy ?? cohort.fguOwners} / ${cohort.bguOwners}`} sub={`${cohort.dualOwners} dual · ${cohort.unattributedOwners} pre-3.0 legacy`} />
        <Stat
          label="Season buyers"
          value={cohort.seasonBuyers.toLocaleString()}
          sub={cohort.seasonAttachPct != null ? `${cohort.seasonAttachPct}% attach` : 'attach pending'}
        />
        <Stat
          label="Season revenue"
          value={seasonRev != null ? `$${seasonRev.toLocaleString()}` : 'pending'}
          sub="lifetime, all storefronts"
        />
        <Stat
          label="Legacy stage owners"
          value={cohort.legacyStageOwners.toLocaleString()}
          sub="pre-FGU era, informational"
        />
      </div>
      {cohort.bySeasonBuyers.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
          {cohort.bySeasonBuyers.map((s) => (
            <span key={s.season}>
              Season {s.season}: <b className="text-slate-200">{s.buyers}</b> buyer{s.buyers === 1 ? '' : 's'}
            </span>
          ))}
        </div>
      )}
    </Section>
  )
}

export function DashboardScreen({ onHome }: { onHome: () => void }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; data: Metrics }
  >({ kind: 'loading' })

  const [buyerState, setBuyerState] = useState<BuyerState>({ kind: 'loading' })

  const load = useCallback(async () => {
    setState({ kind: 'loading' })
    setBuyerState({ kind: 'loading' })
    // Two independent fetches, resolved in parallel — getBuyerProfiles is a
    // separate callable from getDashboardMetrics, so either one failing (or
    // being slow) must never block or blank the other's result.
    await Promise.all([
      callMetrics({}).then(
        (res) => setState({ kind: 'ready', data: res.data }),
        (e) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }),
      ),
      callBuyerProfiles({}).then(
        (res) => setBuyerState({ kind: 'ready', data: res.data }),
        (e) => setBuyerState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }),
      ),
    ])
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="h-full w-full overflow-auto bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-5xl px-5 py-6">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold">FlatoutATC — Funnel</h1>
            {state.kind === 'ready' && (
              <p className="mt-1 text-sm text-slate-400">Tracking from {state.data.metricsStartDate}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold ring-1 ring-white/10 hover:bg-slate-700"
            >
              Refresh
            </button>
            <button
              onClick={onHome}
              className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold ring-1 ring-white/10 hover:bg-slate-700"
            >
              Home
            </button>
          </div>
        </header>

        {state.kind === 'loading' && <p className="text-sm text-slate-400">Loading metrics…</p>}
        {state.kind === 'error' && (
          <div className="rounded-md bg-rose-900/50 px-4 py-3 text-sm text-rose-100 ring-1 ring-rose-500/40">
            Couldn’t load metrics: {state.message}
          </div>
        )}

        {state.kind === 'ready' && <Body data={state.data} buyerState={buyerState} />}
      </div>
    </div>
  )
}

/// A section title with its rolling window, when the function reports one.
function windowTitle(title: string, windowDays?: number): string {
  return windowDays ? `${title} (last ${windowDays} days)` : title
}

function Body({ data, buyerState }: { data: Metrics; buyerState: BuyerState }) {
  return (
    <div className="space-y-6">
      {/* The quarter goal: $750 of net proceeds, Nov 1 to Jan 31. The only
          goal on the page, so it reads first. */}
      {data.quarterGoal === undefined ? null : isPending(data.quarterGoal) ? (
        <PendingCard title="Quarter goal" note={data.quarterGoal.note} />
      ) : (
        <QuarterGoalCard data={data.quarterGoal} fencePosts={data.fencePosts ?? []} />
      )}

      {/* Top-line cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat
          label={`Active (${data.activeWindowDays}d)`}
          value={data.install.active30d.toLocaleString()}
          sub={`${data.install.totalUsers.toLocaleString()} lifetime`}
        />
        <Stat
          label="Base owners (FGU+BGU)"
          value={(data.buyerCohort?.baseOwners ?? data.conversion.buyers).toLocaleString()}
          sub="lifetime"
        />
        <Stat label="Engaged ≥5 sessions" value={`${data.engagement.buckets.ge5.pct}%`} sub={`${data.engagement.buckets.ge5.count.toLocaleString()} users`} />
      </div>

      <AnnouncementComposer />

      {/* Base owners split (FGU vs BGU) + season buyers/attach/revenue.
          "Base owners" above used to render as "FGU buyers" — mislabeled,
          since users.fullGameUnlockDate is set for legacy FGU AND the 3.0 BGU
          alike (GameManager.recordPurchaseStateIfNeeded). This section is the
          honest split, layered from purchases.productId. See buyerCohortCore.ts. */}
      {data.buyerCohort && <BuyerCohortSection cohort={data.buyerCohort} revenueByProduct={data.revenueByProduct} />}

      {/* Engagement-depth curve */}
      <Section title="Engagement depth" note={data.engagement.note}>
        <div className="space-y-2">
          {(['ge2', 'ge3', 'ge5', 'ge10', 'ge25'] as const).map((k) => {
            const b = data.engagement.buckets[k]
            const label = '≥ ' + k.slice(2) + ' sessions'
            return (
              <div key={k} className="flex items-center gap-3 text-xs">
                <div className="w-24 shrink-0 text-slate-400">{label}</div>
                <div className="h-4 flex-1 overflow-hidden rounded bg-slate-800">
                  <div
                    className="h-full rounded bg-sky-500"
                    style={{ width: `${Math.min(100, b.pct)}%` }}
                  />
                </div>
                <div className="w-28 shrink-0 text-right tabular-nums text-slate-300">
                  {b.pct}% · {b.count.toLocaleString()}
                </div>
              </div>
            )
          })}
        </div>
      </Section>

      {/* Insight paragraph — deterministic "look here, not there" read of the
          funnel's own week-over-week numbers. Always rendered ABOVE the funnel
          report itself, every time it's run (Craig's brief, verbatim). */}
      {isPending(data.funnelInsight) ? (
        <PendingCard title="Insight" note={data.funnelInsight.note} />
      ) : (
        <FunnelInsightCard data={data.funnelInsight} />
      )}

      {/* Buyer Ledger — auto-profile of every buyer (getBuyerProfiles), the
          same read triage has been doing by hand: anchor, journey before
          purchase, refund/risk state. See buyerProfileCore.ts. */}
      <BuyersCard state={buyerState} />

      {/* The second session — BACKLOG #126. Retention is the headline because
          it is the same project as conversion and nine times more measurable;
          the day-7 endpoint (#128) sits beside the day-1 figure it replaces. */}
      {isPending(data.retention) ? (
        <PendingCard title="The second session" note={data.retention.note} />
      ) : (
        <Section title={windowTitle('The second session', data.retention.windowDays)} note={data.retention.note}>
          <div className="flex flex-wrap gap-6">
            <Stat
              label="D1 retention"
              value={`${data.retention.d1.pct}%`}
              sub={
                `${data.retention.d1.retained.toLocaleString()} of ${data.retention.d1.cohort.toLocaleString()} · target 20%` +
                (data.retention.sinceStart ? ` · ${data.retention.sinceStart.d1.pct}% since ${data.retention.sinceStart.since}` : '')
              }
            />
            <Stat
              label="D7 retention"
              value={data.retention.d7 ? `${data.retention.d7.pct}%` : 'building'}
              sub={data.retention.d7 ? `${data.retention.d7.retained.toLocaleString()} of ${data.retention.d7.cohort.toLocaleString()}` : 'needs a 7-day-old cohort'}
            />
            <Stat
              label="Bought by day 7"
              value={data.retention.conversion ? `${data.retention.conversion.byDay7Pct}%` : 'building'}
              sub={data.retention.conversion ? `by day 1: ${data.retention.conversion.byDay1Pct}% · n=${data.retention.conversion.cohort.toLocaleString()}` : 'the conversion endpoint'}
            />
          </div>
        </Section>
      )}

      {/* Next Shift — D1 by experiment arm (next_shift_variant), the one
          question the experiment exists to answer, beside the treatment's own
          shown/scheduled/opened funnel. Pending until the switch is on. */}
      {data.nextShiftD1 === undefined ? null : isPending(data.nextShiftD1) ? (
        <PendingCard title="Next Shift" note={data.nextShiftD1.note} />
      ) : (
        <Section title="Next Shift" note={data.nextShiftD1.note}>
          <div className="flex flex-wrap gap-6">
            {data.nextShiftD1.arms.map((a) => (
              <Stat
                key={a.variant}
                label={`${NEXT_SHIFT_ARM_LABEL[a.variant]} D1`}
                value={a.pct != null ? `${a.pct}%` : 'building'}
                sub={
                  `${a.retained.toLocaleString()} of ${a.cohort.toLocaleString()} · ` +
                  `shown ${a.shown.toLocaleString()} · ` +
                  (a.tapped != null ? `tapped ${a.tapped.toLocaleString()} · ` : '') +
                  `scheduled ${a.scheduled.toLocaleString()} · opened ${a.opened.toLocaleString()}`
                }
              />
            ))}
          </div>
          {data.nextShiftD1.byVersion && data.nextShiftD1.byVersion.length > 0 && (
            <NextShiftVersionTable versions={data.nextShiftD1.byVersion} />
          )}
        </Section>
      )}

      {/* The debrief — BACKLOG #123. What players do on the post-level screen,
          by audience and academy/real, each action as a share of that cell's
          shows. 3.4 instrumentation; pending until it is in the field. */}
      {data.postLevelActions === undefined ? null : isPending(data.postLevelActions) ? (
        <PendingCard title="The debrief" note={data.postLevelActions.note} />
      ) : (
        <Section title="The debrief" note={data.postLevelActions.note}>
          <DebriefTable rows={data.postLevelActions.rows} />
        </Section>
      )}

      {/* App Store discovery: search positions, top charts and the App Store
          funnel, each against the previous day */}
      {data.discovery &&
        (isPending(data.discovery) ? (
          <PendingCard title="App Store discovery" note={data.discovery.note} />
        ) : (
          <DiscoveryCard data={data.discovery} />
        ))}

      {/* Acquisition funnel — GA4 BigQuery export, the last 60 days of installs */}
      {isPending(data.funnel) ? (
        <PendingCard title="Acquisition funnel" note={data.funnel.note} />
      ) : (
        <Section title={windowTitle('Acquisition funnel', data.funnel.windowDays)} note={data.funnel.note}>
          <div className="space-y-2">
            {[
              { label: 'Installs', count: data.funnel.installs, pct: 100 },
              { label: 'Onboarded', count: data.funnel.onboarded, pct: data.funnel.onboardedPct },
              { label: 'Viewed purchase', count: data.funnel.viewedPurchase, pct: data.funnel.viewedPurchasePct },
              { label: 'Purchased', count: data.funnel.purchasers, pct: data.funnel.conversionPct },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-3 text-xs">
                <div className="w-28 shrink-0 text-slate-400">{s.label}</div>
                <div className="h-4 flex-1 overflow-hidden rounded bg-slate-800">
                  <div
                    className="h-full rounded bg-emerald-500"
                    style={{ width: `${Math.min(100, s.pct)}%` }}
                  />
                </div>
                <div className="w-28 shrink-0 text-right tabular-nums text-slate-300">
                  {s.pct}% · {s.count.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Users by day — GA4 BigQuery DAU, trailing 30 days, with fence posts */}
      {isPending(data.usersByDay) ? (
        <PendingCard title="Users by day" note={data.usersByDay.note} />
      ) : (
        <Section title="Users by day (GA4, 30d)" note={data.usersByDay.note}>
          <FenceLegend fencePosts={data.fencePosts ?? []} />
          <div className="mt-3">
            <CountTrend
              title="Daily active users"
              points={data.usersByDay.series.map((p) => ({ date: p.date, v: p.users }))}
              color="#22d3ee"
              unit="users"
              fencePosts={data.fencePosts ?? []}
            />
          </div>
        </Section>
      )}

      {/* Level quality — LIVE behavioral scorecard + the version split */}
      {isPending(data.levelScorecard) ? (
        <PendingCard title="Level quality" note={data.levelScorecard.note} />
      ) : (
        <LevelScorecardCard data={data.levelScorecard} />
      )}

      {/* Time to convert, and the money: every storefront's proceeds minus
          Apple Search Ads spend, trailing 30 days */}
      <div className="grid gap-3 sm:grid-cols-2">
        {isPending(data.timeToConvert) ? (
          <PendingCard title="Time to convert" note={data.timeToConvert.note} />
        ) : (
          <Section title="Time to convert" note={data.timeToConvert.note}>
            {data.timeToConvert.avgHours == null ? (
              <p className="text-sm text-slate-400">No conversions in-window yet</p>
            ) : (
              <p className="text-sm text-slate-300">
                avg <b>{data.timeToConvert.avgHours.toFixed(1)}h</b> · median{' '}
                <b>{data.timeToConvert.medianHours ?? '—'}h</b> · n={data.timeToConvert.converted}
              </p>
            )}
          </Section>
        )}
        {isPending(data.revenueVsSpend) ? (
          <PendingCard title="Proceeds − ASA spend" note={data.revenueVsSpend.note} />
        ) : (
          <MoneyCard rv={data.revenueVsSpend} />
        )}
      </div>

      <Section
        title={`Crashes (${data.crashes.windowDays}d)`}
        note={data.crashes.available ? data.crashes.note : data.crashes.reason}
      >
        {data.crashes.available ? (
          <>
            <div className="flex gap-8">
              <div>
                <div
                  className={
                    'text-2xl font-bold tabular-nums ' +
                    (data.crashes.fatalEvents > 0 ? 'text-rose-400' : 'text-emerald-400')
                  }
                >
                  {data.crashes.fatalEvents}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  fatal · {data.crashes.fatalIssues} issue{data.crashes.fatalIssues === 1 ? '' : 's'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold tabular-nums text-slate-300">
                  {data.crashes.issues}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  active issues · {data.crashes.events} events
                </div>
              </div>
            </div>
            {data.crashes.topIssues.length > 0 && (
              <ul className="mt-3 space-y-1">
                {data.crashes.topIssues.map((c, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate text-slate-300">
                      {c.fatal && <span className="mr-1 text-rose-400">●</span>}
                      {c.subtitle || c.title}
                    </span>
                    <span className="shrink-0 font-mono text-slate-500">{c.events}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-400">Crash data unavailable.</p>
        )}
      </Section>

      {isPending(data.deeplinks) ? (
        <PendingCard title="Deep links" note={data.deeplinks.note} />
      ) : (
        <DeepLinksSection data={data.deeplinks} />
      )}

      <MarginTrend history={data.history} fencePosts={data.fencePosts ?? []} />

      <TrendGrid history={data.history} fencePosts={data.fencePosts ?? []} />

      <p className="pt-2 text-[11px] text-slate-500">
        Generated {new Date(data.generatedAt).toLocaleString()}. Funnel, retention and time to convert
        come from the GA4 BigQuery export with test traffic excluded; money from the App Store
        Connect and Apple Search Ads daily pulls.
      </p>
    </div>
  )
}

/// Every storefront's ASC proceeds (FX-converted) minus Apple Search Ads spend,
/// trailing 30 days, with the purchases webhook's real-time gross beneath. The
/// US-storefront fields stay in the payload for the business page and the
/// history series.
function MoneyCard({ rv }: { rv: RevenueVsSpendLive }) {
  const proceeds = rv.allStorefrontsProceedsUSD30d ?? rv.proceedsUSD30d
  const margin = rv.allStorefrontsContributionMarginUSD30d ?? null
  const money = (v: number) => `$${Math.abs(v).toLocaleString()}`
  return (
    <Section title="Proceeds − ASA spend (30d, all storefronts)" note={rv.note}>
      {margin == null ? (
        <>
          <p className="text-2xl font-bold tabular-nums">{money(proceeds)}</p>
          <p className="text-[11px] text-slate-500">
            {rv.paidUnits30d.toLocaleString()} sales · spend pending
          </p>
        </>
      ) : (
        <>
          <p className={`text-2xl font-bold tabular-nums ${margin >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            {margin >= 0 ? '+' : '−'}
            {money(margin)}
          </p>
          <p className="text-[11px] text-slate-500">
            {money(proceeds)} proceeds − {money(rv.spendUSD30d ?? 0)} spend · {rv.paidUnits30d.toLocaleString()} sales
          </p>
        </>
      )}
      {rv.allStorefrontsGrossUSD30d != null && (
        <p className="mt-2 border-t border-slate-800 pt-2 text-[11px] text-slate-500">
          Real-time gross from the purchases webhook, before Apple's commission:{' '}
          {money(rv.allStorefrontsGrossUSD30d)}. The proceeds above lag 1 to 3 days.
        </p>
      )}
    </Section>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg bg-slate-900 p-3 ring-1 ring-white/10">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>}
    </div>
  )
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-slate-900 p-4 ring-1 ring-white/10">
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      {children}
      {note && <p className="mt-3 text-[11px] text-slate-500">{note}</p>}
    </div>
  )
}

const fenceColor = (type: string) =>
  type === 'asa' ? '#fbbf24' : type === 'campaign' ? '#34d399' : '#60a5fa'

// Position dated fence posts along a series' date span (future dates pin right).
function fenceMarks(pts: { date: string }[], fencePosts: FencePost[], W: number, pad: number) {
  if (pts.length < 1) return []
  const t0 = Date.parse(pts[0]!.date)
  const t1 = Date.parse(pts[pts.length - 1]!.date)
  const tspan = t1 - t0 || 1
  return fencePosts.map((f) => {
    const frac = Math.max(0, Math.min(1, (Date.parse(f.date) - t0) / tspan))
    return { ...f, fx: pad + frac * (W - 2 * pad) }
  })
}

// One shared legend for the fence-post lines (so we don't repeat it per chart).
function FenceLegend({ fencePosts }: { fencePosts: FencePost[] }) {
  if (fencePosts.length === 0) return null
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
      {fencePosts.map((f, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: fenceColor(f.type) }} />
          {f.date.slice(5)} {f.label}
        </span>
      ))}
    </div>
  )
}

// A single percentage trend tile: auto-scaled line + dashed fence-post verticals
// (hover shows the label). Renders a "builds daily" state until it has 2+ points.
function PctTrend({
  title,
  history,
  field,
  color,
  fencePosts,
}: {
  title: string
  history: HistoryPoint[]
  field: 'engagedGe5Pct' | 'engagedGe25Pct' | 'd1Pct' | 'conversionPct'
  color: string
  fencePosts: FencePost[]
}) {
  const pts = history
    .filter((h) => typeof h[field] === 'number')
    .map((h) => ({ date: h.date, v: h[field] as number }))
  if (pts.length < 2) {
    return (
      <div className="rounded-lg bg-slate-900 p-3 ring-1 ring-white/10">
        <h3 className="text-xs font-semibold text-slate-300">{title}</h3>
        <p className="mt-2 text-[11px] text-slate-500">
          Builds daily — {pts.length} snapshot{pts.length === 1 ? '' : 's'} so far.
        </p>
      </div>
    )
  }
  const W = 300
  const H = 64
  const pad = 4
  const vals = pts.map((p) => p.v)
  const rawSpan = Math.max(Math.max(...vals) - Math.min(...vals), 1)
  const min = Math.max(0, Math.min(...vals) - rawSpan * 0.15)
  const max = Math.min(100, Math.max(...vals) + rawSpan * 0.15)
  const span = max - min || 1
  const x = (i: number) => pad + (i * (W - 2 * pad)) / (pts.length - 1)
  const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad)
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ')
  const latest = vals[vals.length - 1]!
  const marks = fenceMarks(pts, fencePosts, W, pad)
  return (
    <div className="rounded-lg bg-slate-900 p-3 ring-1 ring-white/10">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold text-slate-300">{title}</h3>
        <span className="text-sm font-bold tabular-nums" style={{ color }}>
          {latest.toFixed(1)}%
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mt-1 w-full" style={{ height: 64 }}>
        <text x={pad} y={8} fill="#475569" fontSize="7">{max.toFixed(0)}%</text>
        <text x={pad} y={H - 2} fill="#475569" fontSize="7">{min.toFixed(0)}%</text>
        {marks.map((m, i) => (
          <line key={i} x1={m.fx} x2={m.fx} y1={0} y2={H} stroke={fenceColor(m.type)} strokeWidth="1" strokeDasharray="2 2" opacity="0.7">
            <title>{m.date.slice(5)} {m.label}</title>
          </line>
        ))}
        <path d={d} fill="none" stroke={color} strokeWidth="2" />
      </svg>
      <p className="mt-1 text-[11px] text-slate-500">Latest {latest.toFixed(1)}% · {pts.length} days</p>
    </div>
  )
}

// An absolute-count trend line (mirrors PctTrend, but no 0–100 clamp and formats
// with thousands separators). Takes a pre-built {date, v}[] series so it can chart
// any daily count (used for GA4 daily active users).
function CountTrend({
  title,
  points,
  color,
  unit,
  fencePosts,
}: {
  title: string
  points: { date: string; v: number }[]
  color: string
  unit: string
  fencePosts: FencePost[]
}) {
  const pts = points.filter((p) => typeof p.v === 'number')
  if (pts.length < 2) {
    return (
      <div className="rounded-lg bg-slate-900 p-3 ring-1 ring-white/10">
        <h3 className="text-xs font-semibold text-slate-300">{title}</h3>
        <p className="mt-2 text-[11px] text-slate-500">
          Builds daily — {pts.length} day{pts.length === 1 ? '' : 's'} so far.
        </p>
      </div>
    )
  }
  const W = 300
  const H = 64
  const pad = 4
  const vals = pts.map((p) => p.v)
  const rawSpan = Math.max(Math.max(...vals) - Math.min(...vals), 1)
  const min = Math.max(0, Math.min(...vals) - rawSpan * 0.15)
  const max = Math.max(...vals) + rawSpan * 0.15
  const span = max - min || 1
  const x = (i: number) => pad + (i * (W - 2 * pad)) / (pts.length - 1)
  const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad)
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ')
  const latest = vals[vals.length - 1]!
  const avg = Math.round(vals.reduce((s, v) => s + v, 0) / vals.length)
  const marks = fenceMarks(pts, fencePosts, W, pad)
  return (
    <div className="rounded-lg bg-slate-900 p-3 ring-1 ring-white/10">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold text-slate-300">{title}</h3>
        <span className="text-sm font-bold tabular-nums" style={{ color }}>
          {latest.toLocaleString()}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mt-1 w-full" style={{ height: 64 }}>
        <text x={pad} y={8} fill="#475569" fontSize="7">{Math.round(max).toLocaleString()}</text>
        <text x={pad} y={H - 2} fill="#475569" fontSize="7">{Math.round(min).toLocaleString()}</text>
        {marks.map((m, i) => (
          <line key={i} x1={m.fx} x2={m.fx} y1={0} y2={H} stroke={fenceColor(m.type)} strokeWidth="1" strokeDasharray="2 2" opacity="0.7">
            <title>{m.date.slice(5)} {m.label}</title>
          </line>
        ))}
        <path d={d} fill="none" stroke={color} strokeWidth="2" />
      </svg>
      <p className="mt-1 text-[11px] text-slate-500">
        Latest {latest.toLocaleString()} {unit} · avg {avg.toLocaleString()} · {pts.length} days
      </p>
    </div>
  )
}

// 2×2 small-multiples of the behavioral % trends, with one shared fence legend.
function TrendGrid({ history, fencePosts }: { history: HistoryPoint[]; fencePosts: FencePost[] }) {
  return (
    <Section title="Behavioral trends">
      <FenceLegend fencePosts={fencePosts} />
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <PctTrend title="Engaged ≥5 sessions" history={history} field="engagedGe5Pct" color="#38bdf8" fencePosts={fencePosts} />
        <PctTrend title="Engaged ≥25 sessions" history={history} field="engagedGe25Pct" color="#818cf8" fencePosts={fencePosts} />
        <PctTrend title="D1 retention" history={history} field="d1Pct" color="#34d399" fencePosts={fencePosts} />
        <PctTrend title="Conversion (purchased)" history={history} field="conversionPct" color="#fb923c" fencePosts={fencePosts} />
      </div>
    </Section>
  )
}

function MarginTrend({ history, fencePosts }: { history: HistoryPoint[]; fencePosts: FencePost[] }) {
  // Every storefront once that series (from 2026-09-25) has 30 days; until
  // then the US-storefront series, whose history runs back to June.
  const allPts = history.filter((h) => typeof h.marginAllStorefrontsUSD30d === 'number')
  const useAll = allPts.length >= 30
  const pts = (useAll ? allPts : history.filter((h) => typeof h.marginUSD30d === 'number')).map((h) => ({
    date: h.date,
    m: (useAll ? h.marginAllStorefrontsUSD30d : h.marginUSD30d) as number,
  }))
  const scope = useAll
    ? 'all storefronts'
    : `US storefront; switches to all storefronts when that series has 30 days (${30 - allPts.length} to go)`
  if (pts.length < 2) {
    return (
      <Section title="Margin trend (30d rolling)">
        <p className="text-sm text-slate-400">
          Builds daily — {pts.length} snapshot{pts.length === 1 ? '' : 's'} so far. A new point
          lands every day from the scheduled snapshot.
        </p>
      </Section>
    )
  }
  const W = 600
  const H = 80
  const pad = 4
  const vals = pts.map((p) => p.m)
  const min = Math.min(0, ...vals)
  const max = Math.max(0, ...vals)
  const span = max - min || 1
  const x = (i: number) => pad + (i * (W - 2 * pad)) / (pts.length - 1)
  const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad)
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.m).toFixed(1)}`).join(' ')
  const latest = vals[vals.length - 1]!
  // Fence-post overlays (releases, ASA changes), positioned by date across the
  // series span; future dates (e.g. a same-day release) pin to the right edge.
  const marks = fenceMarks(pts, fencePosts ?? [], W, pad)
  return (
    <Section
      title="Margin trend (30d rolling)"
      note={`Each point = trailing-30d contribution margin on that day (${scope}).`}
    >
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 80 }}>
        <line
          x1={pad}
          x2={W - pad}
          y1={y(0)}
          y2={y(0)}
          stroke="#475569"
          strokeWidth="1"
          strokeDasharray="4 4"
        />
        {marks.map((m, i) => (
          <line
            key={i}
            x1={m.fx}
            x2={m.fx}
            y1={0}
            y2={H}
            stroke={fenceColor(m.type)}
            strokeWidth="1"
            strokeDasharray="2 2"
            opacity="0.85"
          />
        ))}
        <path d={d} fill="none" stroke={latest >= 0 ? '#34d399' : '#fb7185'} strokeWidth="2" />
      </svg>
      {marks.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
          {marks.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-sm"
                style={{ background: fenceColor(m.type) }}
              />
              {m.date.slice(5)} {m.label}
            </span>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">
        Latest {latest >= 0 ? '+' : '−'}${Math.abs(latest).toLocaleString()} · {pts.length} days ·
        zero line dashed · lines = releases (blue) / ASA changes (amber)
      </p>
    </Section>
  )
}

const MODE_LABEL: Record<LevelMode, string> = {
  air: 'Air',
  ground: 'Ground',
  academy: 'Academy',
  tutorial: 'Tutorial',
  live: 'Live',
  sar: 'SAR',
  oceanic: 'Oceanic',
}

function pctStr(v: number | null): string {
  return v == null ? '—' : `${v}%`
}

// Behavioral level scorecard: per-level finish/win/rating with weak levels
// flagged (rose rows), and the version finish split that surfaces fix impact the
// lifetime star average can't. Win is muted for live/SAR (open-ended modes).
// Click a row to drill into its per-version breakdown.
// Displayed finish%: prefer the outcome-aware (completed+busted)/starts read
// once 3.0+ telemetry is present for that row/slice; else the old ends/starts.
function displayFinishPct(r: { hasOutcomeData: boolean; outcomeFinishPct: number | null; finishPct: number | null }): number | null {
  return r.hasOutcomeData ? r.outcomeFinishPct : r.finishPct
}

function LevelScorecardCard({ data }: { data: LevelScorecardLive }) {
  const [open, setOpen] = useState<string | null>(null)
  const asOf = new Date(data.asOf)
  const asOfStr = isNaN(asOf.getTime()) ? '—' : asOf.toLocaleString()
  const rows = data.levels.slice(0, 30)
  const split = data.versionSplit
  const splitLabel = split ? `${split.lo}→${split.hi} finish` : 'finish split'
  return (
    <div className="rounded-lg bg-slate-900 p-4 ring-1 ring-white/10">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">
          Level quality{data.concernCount > 0 ? ` · ${data.concernCount} to tune` : ''}
        </h2>
        <span className="text-[11px] text-slate-500">
          behavioral · {data.windowDays}d · as of {asOfStr}
          {data.cached ? ' (cached)' : ''}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead className="text-slate-500">
            <tr>
              <th className="py-1 pr-2 font-medium">Level</th>
              <th className="py-1 px-2 text-right font-medium">Starts</th>
              <th className="py-1 px-2 text-right font-medium">Finish</th>
              <th className="py-1 px-2 text-right font-medium">Abandon</th>
              <th className="py-1 px-2 text-right font-medium">Win</th>
              <th className="py-1 px-2 text-right font-medium" title="Bayes-shrunk: (10·m + sum)/(10 + n), m = unweighted mean of level means. Greyed under n=5 (mostly prior). Hover a value for the raw mean.">★</th>
              <th className="py-1 px-2 text-right font-medium" title="Share of ratings at 4-5★ — readable around n≈15-20 where a mean needs ~100.">Top-2</th>
              <th className="py-1 px-2 text-right font-medium" title="Share of ratings at 1-2★ — how many players it actually burned. '—' = feed predates the buckets.">1-2★</th>
              <th className="py-1 pl-2 text-right font-medium">{splitLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const vLo = split ? r.byVersion.find((v) => v.version === split.lo) : undefined
              const vHi = split ? r.byVersion.find((v) => v.version === split.hi) : undefined
              const splitCells =
                vLo && vHi ? `${pctStr(displayFinishPct(vLo))}→${pctStr(displayFinishPct(vHi))}` : '—'
              const winMuted = r.mode === 'live' || r.mode === 'sar' || r.mode === 'oceanic'
              const isOpen = open === r.levelId
              const rowTone = r.devLowSignal ? '' : r.concern ? 'bg-rose-500/10' : ''
              return (
                <Fragment key={r.levelId}>
                  <tr
                    className={`cursor-pointer border-t border-white/5 hover:bg-white/5 ${rowTone}`}
                    onClick={() => setOpen(isOpen ? null : r.levelId)}
                  >
                    <td className="py-1 pr-2">
                      <span className="mr-1 text-slate-600">{isOpen ? '▾' : '▸'}</span>
                      <span className={r.concern ? 'font-semibold text-rose-200' : 'text-slate-200'}>{r.levelId}</span>
                      <span className="ml-1 text-[10px] text-slate-500">{MODE_LABEL[r.mode]}</span>
                      {r.devLowSignal && (
                        <span
                          className="ml-1 rounded bg-slate-700/60 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400"
                          title="Traffic looks like dev/test devices (one device dominates, or a thin cohort of <5 players with >20 starts) — excluded from concern ranking."
                        >
                          dev/low-signal
                        </span>
                      )}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums text-slate-300">{r.starts.toLocaleString()}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-slate-300">{pctStr(displayFinishPct(r))}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-slate-400">
                      {r.hasOutcomeData ? pctStr(r.abandonRate) : '—'}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums text-slate-400">{winMuted ? '—' : pctStr(r.winPct)}</td>
                    <td
                      className={`py-1 px-2 text-right tabular-nums ${r.ratings < 5 ? 'text-slate-600' : 'text-slate-400'}`}
                      title={r.avgStar == null ? undefined : `raw ${r.avgStar.toFixed(2)} · n=${r.ratings}${r.ratings < 5 ? ' · mostly prior' : ''}`}
                    >
                      {(r.bayesStar ?? r.avgStar) == null ? '—' : (r.bayesStar ?? r.avgStar)!.toFixed(1)}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums text-slate-400">{pctStr(r.top2BoxPct ?? null)}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-slate-400">{pctStr(r.lowSharePct ?? null)}</td>
                    <td className="py-1 pl-2 text-right tabular-nums text-slate-400">{splitCells}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-white/5 bg-slate-950/40">
                      <td colSpan={9} className="px-3 py-2">
                        <div className="mb-1 text-[11px] text-slate-500">
                          By version · {r.players.toLocaleString()} players · {r.ends.toLocaleString()} outcomes · {r.ratings} ratings
                        </div>
                        <table className="w-full text-left text-[11px]">
                          <thead className="text-slate-600">
                            <tr>
                              <th className="py-0.5 pr-2 font-medium">Version</th>
                              <th className="py-0.5 px-2 text-right font-medium">Starts</th>
                              <th className="py-0.5 px-2 text-right font-medium">Finish</th>
                              <th className="py-0.5 px-2 text-right font-medium">Abandon</th>
                              <th className="py-0.5 px-2 text-right font-medium">Win</th>
                              <th className="py-0.5 px-2 text-right font-medium">★</th>
                              <th className="py-0.5 pl-2 text-right font-medium">Ratings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.byVersion.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="py-1 text-slate-500">No per-version data.</td>
                              </tr>
                            ) : (
                              r.byVersion.map((v) => (
                                <tr key={v.version} className="border-t border-white/5">
                                  <td className="py-0.5 pr-2 text-slate-300">{v.version}</td>
                                  <td className="py-0.5 px-2 text-right tabular-nums text-slate-400">{v.starts.toLocaleString()}</td>
                                  <td className="py-0.5 px-2 text-right tabular-nums text-slate-400">{pctStr(displayFinishPct(v))}</td>
                                  <td className="py-0.5 px-2 text-right tabular-nums text-slate-400">
                                    {v.hasOutcomeData ? pctStr(v.abandonRate) : '—'}
                                  </td>
                                  <td className="py-0.5 px-2 text-right tabular-nums text-slate-400">{winMuted ? '—' : pctStr(v.winPct)}</td>
                                  <td className="py-0.5 px-2 text-right tabular-nums text-slate-400">{v.avgStar == null ? '—' : v.avgStar.toFixed(1)}</td>
                                  <td className="py-0.5 pl-2 text-right tabular-nums text-slate-400">{v.ratings}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {data.levels.length > rows.length && (
        <p className="mt-2 text-[11px] text-slate-500">
          Showing top {rows.length} of {data.levels.length} levels by starts. Click a row for its per-version breakdown.
        </p>
      )}
      <p className="mt-3 text-[11px] text-slate-500">{data.note}</p>
    </div>
  )
}

// --- Buyer Ledger -----------------------------------------------------------

function Badge({ tone, title, children }: { tone: 'rose' | 'amber' | 'slate'; title?: string; children: React.ReactNode }) {
  const cls =
    tone === 'rose'
      ? 'bg-rose-500/20 text-rose-200 ring-rose-400/40'
      : tone === 'amber'
        ? 'bg-amber-400/10 text-amber-300 ring-amber-400/20'
        : 'bg-slate-700/60 text-slate-400 ring-white/10'
  return (
    <span title={title} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${cls}`}>
      {children}
    </span>
  )
}

// formatMinutesShort now lives in src/util/buyerLedger.ts (imported above) —
// extracted so it's testable and shared with the compact row summary.

function DimStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 sm:block">
      <span className="text-slate-500">{label}</span>{' '}
      <span className="text-slate-200">{value}</span>
    </div>
  )
}

function BuyerCard({ profile }: { profile: BuyerProfile }) {
  const p = profile
  const purchaseDate = p.anchor.purchaseDate ? new Date(p.anchor.purchaseDate) : null
  const dateStr = purchaseDate && !isNaN(purchaseDate.getTime()) ? purchaseDate.toLocaleString() : 'unknown date'
  const refunded = p.refund.outcome === 'refunded'
  const priceStr = p.anchor.price != null ? `${p.anchor.currency ?? 'USD'} ${p.anchor.price.toFixed(2)}` : '—'
  const wt = p.journey.before.wallTouches
  const dv = p.journey.before.doorViews
  const doorCount = (dv?.bgu.reduce((s, r) => s + r.count, 0) ?? 0) + (dv?.season.reduce((s, r) => s + r.count, 0) ?? 0)

  return (
    <div
      className={`rounded-lg p-3 ring-1 ${
        refunded ? 'bg-rose-500/10 ring-rose-400/40' : 'bg-slate-950/40 ring-white/10'
      }`}
    >
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[11px] text-slate-500">
          {dateStr} · {p.anchor.productIdShort ?? '?'} · {priceStr} · {p.anchor.environment ?? '?'}
          {p.source !== 'trigger' && ` · ${p.source}`}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {p.refund.outcome === 'refunded' && <Badge tone="rose">Refunded</Badge>}
          {p.refund.outcome === 'requested' && <Badge tone="amber">Consumption request</Badge>}
          {p.refund.riskFlags.map((f) => (
            <Badge key={f} tone="amber" title="Two or more co-occurring signals — see buyerProfileCore.ts's composeRiskFlags">
              {f.replace(/_/g, ' ')}
            </Badge>
          ))}
          {!p.dataHorizon.trailComplete && (
            <Badge tone="slate" title="The BigQuery uid-tagging trail doesn't reach back to install — before-purchase counts may be a floor, not the true total.">
              partial trail
            </Badge>
          )}
        </div>
      </div>

      <p className="text-[13px] leading-relaxed text-slate-100">{p.narrative}</p>

      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] sm:grid-cols-4">
        <DimStat label="uid" value={p.identity.uid ?? '—'} />
        <DimStat label="place" value={p.identity.purchasePlace ?? '—'} />
        <DimStat label="time to buy" value={p.journey.before.minutesToPurchase != null ? formatMinutesShort(p.journey.before.minutesToPurchase) : '—'} />
        <DimStat
          label="when"
          value={p.journey.before.weekday ? `${p.journey.before.weekday} ${p.journey.before.purchaseHourLocal}:00${p.journey.before.timeBasis === 'utc' ? ' UTC' : ''}` : '—'}
        />
        <DimStat label="sessions" value={p.journey.before.sessions != null ? String(p.journey.before.sessions) : '—'} />
        <DimStat
          label="levels"
          value={p.journey.before.levelStarts != null ? `${p.journey.before.levelStarts} starts, ${p.journey.before.distinctLevels ?? 0} distinct` : '—'}
        />
        <DimStat
          label="academy"
          value={p.journey.before.academyLessons != null ? `${p.journey.before.academyLessons}/10${p.journey.before.academyCompleted ? ' · graduated' : ''}` : '—'}
        />
        <DimStat label="live plays" value={p.journey.before.livePlays != null ? String(p.journey.before.livePlays) : '—'} />
        <DimStat label="wall touches" value={wt ? `${wt.count}${wt.reasons.length ? ` (${wt.reasons.join(', ')})` : ''}` : '—'} />
        <DimStat label="door views" value={dv ? String(doorCount) : '—'} />
        <DimStat
          label="after D1 / D7"
          value={`${p.journey.after.playsAfterD1 ?? '?'} / ${p.journey.after.playsAfterD7 ?? '?'}`}
        />
      </div>
      {p.journey.before.experimentArms.length > 0 && (
        <p className="mt-1.5 text-[10px] text-slate-500">
          {p.journey.before.experimentArms.map((a) => `${a.experiment}: ${a.variant}`).join(' · ')}
        </p>
      )}
    </div>
  )
}

// Default rows shown before the "Show all N" toggle — keeps the section
// short enough that the Acquisition funnel below stays one scroll away
// (Craig's complaint: 69 stacked cards to scroll past).
const BUYER_ROW_PAGE_SIZE = 10

function BuyerFilterChips({
  counts,
  active,
  onChange,
}: {
  counts: Record<BuyerFilterKey, number>
  active: BuyerFilterKey
  onChange: (key: BuyerFilterKey) => void
}) {
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {BUYER_FILTER_KEYS.map((key) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={
            'rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition-colors ' +
            (active === key
              ? 'bg-sky-500/20 text-sky-200 ring-sky-400/40'
              : 'bg-slate-800/60 text-slate-400 ring-white/10 hover:text-slate-200')
          }
        >
          {BUYER_FILTER_LABELS[key]} <span className="tabular-nums opacity-70">{counts[key]}</span>
        </button>
      ))}
    </div>
  )
}

/// One compact table row for a buyer, expanding inline to the full
/// BuyerCard (narrative + dimension grid) on click. `colSpan` matches the
/// header's column count.
function BuyerTableRow({
  profile,
  nowMs,
  isOpen,
  onToggle,
  colSpan,
}: {
  profile: BuyerProfile
  nowMs: number
  isOpen: boolean
  onToggle: () => void
  colSpan: number
}) {
  const row = summarizeBuyerRow(profile, nowMs)
  return (
    <Fragment>
      <tr
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        className={'cursor-pointer border-t border-white/5 hover:bg-white/5 ' + (isOpen ? 'bg-white/5' : '')}
      >
        <td className="whitespace-nowrap px-2.5 py-1.5 text-slate-400">{row.dateShort}</td>
        <td className="max-w-[10rem] truncate px-2.5 py-1.5 text-slate-200" title={row.product}>
          {row.product}
        </td>
        <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums text-slate-300">{row.price}</td>
        <td className="px-2.5 py-1.5 text-slate-400">{row.country}</td>
        <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums text-slate-300">{row.timeToBuy}</td>
        <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums text-slate-300">{row.wallTouches}</td>
        <td className="px-2.5 py-1.5">
          <div className="flex flex-wrap gap-1">
            {row.refundOutcome === 'refunded' && <Badge tone="rose">Refunded</Badge>}
            {row.refundOutcome === 'requested' && <Badge tone="amber" title="Consumption request filed, not yet refunded">Req</Badge>}
            {row.riskFlagCount > 0 && (
              <Badge tone="amber" title="Two or more co-occurring risk signals — expand the row for which ones">
                {row.riskFlagCount} flag{row.riskFlagCount === 1 ? '' : 's'}
              </Badge>
            )}
            {!row.trailComplete && (
              <Badge tone="slate" title="The BigQuery uid-tagging trail doesn't reach back to install — before-purchase counts may be a floor, not the true total.">
                trail
              </Badge>
            )}
          </div>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-t border-white/5 bg-slate-950/60">
          <td colSpan={colSpan} className="p-2">
            <BuyerCard profile={profile} />
          </td>
        </tr>
      )}
    </Fragment>
  )
}

const BUYER_TABLE_COLS = 7

function BuyersCard({ state }: { state: BuyerState }) {
  const [filter, setFilter] = useState<BuyerFilterKey>('all')
  const [showAll, setShowAll] = useState(false)
  const [expandedTxn, setExpandedTxn] = useState<string | null>(null)

  // Switching filter chips with a stale "show all"/expansion left open from
  // the PREVIOUS chip defeats the point of keeping this section short.
  useEffect(() => {
    setShowAll(false)
    setExpandedTxn(null)
  }, [filter])

  if (state.kind === 'loading') return <PendingCard title="Buyers" note="Loading the buyer ledger…" />
  if (state.kind === 'error') return <PendingCard title="Buyers" note={`Failed to load: ${state.message}`} />

  const { totals, profiles, windowNote } = state.data
  const nowMs = Date.now()
  const counts = countBuyerFilters(profiles, nowMs)
  const filtered = sortBuyersNewestFirst(filterBuyers(profiles, filter, nowMs))
  const visible = filtered.slice(0, showAll ? filtered.length : BUYER_ROW_PAGE_SIZE)

  return (
    <div className="rounded-lg bg-slate-900 p-4 ring-1 ring-white/10">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Buyers</h2>
        <span className="text-[11px] text-slate-500">deterministic-v1 · {windowNote}</span>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Buyers" value={totals.buyers.toLocaleString()} />
        <Stat label="Countries" value={totals.countries.toLocaleString()} />
        <Stat
          label="Median time to buy"
          value={totals.medianMinutesToPurchase != null ? formatMinutesShort(totals.medianMinutesToPurchase) : '—'}
        />
        <Stat label="Touched paywall first" value={totals.wallTouchPct != null ? `${totals.wallTouchPct}%` : '—'} />
        <Stat
          label="Refunds"
          value={totals.refunds.toLocaleString()}
          sub={totals.buyers > 0 ? `${Math.round((100 * totals.refunds) / totals.buyers)}% of buyers` : undefined}
        />
      </div>

      <BuyerFilterChips counts={counts} active={filter} onChange={setFilter} />

      {profiles.length === 0 ? (
        <p className="text-sm text-slate-400">No buyer profiles yet.</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400">No buyers match this filter.</p>
      ) : (
        <div className="overflow-x-auto rounded-md bg-slate-950/40 ring-1 ring-white/5">
          <table className="w-full min-w-[640px] text-[11px]">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="px-2.5 py-1.5 font-medium">Date</th>
                <th className="px-2.5 py-1.5 font-medium">Product</th>
                <th className="px-2.5 py-1.5 font-medium">Price</th>
                <th className="px-2.5 py-1.5 font-medium">Country</th>
                <th className="px-2.5 py-1.5 font-medium">Time to buy</th>
                <th className="px-2.5 py-1.5 font-medium">Touches</th>
                <th className="px-2.5 py-1.5 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <BuyerTableRow
                  key={p.anchor.txn}
                  profile={p}
                  nowMs={nowMs}
                  isOpen={expandedTxn === p.anchor.txn}
                  onToggle={() => setExpandedTxn(expandedTxn === p.anchor.txn ? null : p.anchor.txn)}
                  colSpan={BUYER_TABLE_COLS}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > BUYER_ROW_PAGE_SIZE && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-[11px] font-medium text-sky-300 hover:text-sky-200"
        >
          {showAll ? 'Show fewer' : `Show all ${filtered.length}`}
        </button>
      )}
    </div>
  )
}

// Detector key -> a short, human label for the "what fired" chip row. Falls
// back to the raw key (with underscores turned to spaces) for forward-compat
// if a new detector key shows up before this map is updated.
const DETECTOR_LABEL: Record<string, string> = {
  freshness_guard: 'stale data',
  floor_breach: 'under floor',
  paywall_stagnation: 'paywall stagnant',
  largest_regression: 'biggest drop',
  largest_improvement: 'biggest gain',
  volume_anomaly: 'volume anomaly',
  ab_watch: 'A/B watch',
}

// The insight paragraph — highlighted, sits ABOVE the funnel report every
// time it's run. Deterministic (engine: 'deterministic-v1'), never an LLM
// call; the chips just name which detector(s) produced the paragraph below.
function FunnelInsightCard({ data }: { data: FunnelInsightLive }) {
  const asOf = new Date(data.asOf)
  const asOfStr = isNaN(asOf.getTime()) ? '—' : asOf.toLocaleString()
  return (
    <div className="rounded-lg bg-gradient-to-br from-amber-500/10 to-slate-900 p-4 ring-1 ring-amber-400/30">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-100">
          <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
          Insight
        </h2>
        <span className="text-[11px] text-slate-500">
          {data.engine} · as of {asOfStr}
          {data.cached ? ' (cached)' : ''}
        </span>
      </div>
      <p className="text-[13px] leading-relaxed text-slate-100">{data.paragraph}</p>
      {data.firedDetectors.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {data.firedDetectors.map((key) => (
            <span
              key={key}
              className="rounded bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300 ring-1 ring-amber-400/20"
            >
              {DETECTOR_LABEL[key] ?? key.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">{data.note}</p>
    </div>
  )
}

const NEXT_SHIFT_ARM_LABEL: Record<NextShiftVariant, string> = {
  control: 'Control',
  treatment: 'Treatment',
}

// The eight post-level actions in the app's own order; `shown` is the
// denominator column, the rest are taps.
const POST_LEVEL_ACTION_COLUMNS: { key: PostLevelAction; label: string }[] = [
  { key: 'shown', label: 'Shown' },
  { key: 'continue', label: 'Continue' },
  { key: 'try_again', label: 'Try again' },
  { key: 'play_again', label: 'Play again' },
  { key: 'hand_over', label: 'Hand over' },
  { key: 'back_to_tower', label: 'Back to tower' },
  { key: 'share', label: 'Share' },
  { key: 'rate', label: 'Rate' },
]
const POST_LEVEL_AUDIENCE_LABEL: Record<string, string> = {
  buyer: 'Buyer',
  free_with_plays: 'Free, plays left',
  free_out_of_plays: 'Free, out of plays',
}

// One table row per (audience, academy/real) cell, columns = the eight
// actions as count over share of shown. Rows arrive already sorted by the
// server (audience, real before academy, action), so the first sighting of a
// cell fixes its position.
/// Next Shift by app version (Craig, 2026-09-23): 3.5 moves the card beside
/// Continue on a landscape phone, where 3.4 clipped its reminder button, so the
/// two cards are read apart. D1 is by the version the install started on; the
/// card counts are by the version that showed the card.
function NextShiftVersionTable({ versions }: { versions: NextShiftVersionArms[] }) {
  const cols: { key: 'shown' | 'tapped' | 'scheduled' | 'opened'; label: string }[] = [
    { key: 'shown', label: 'Shown' },
    { key: 'tapped', label: 'Tapped' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'opened', label: 'Opened' },
  ]
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-400">
            <th className="py-1 font-normal">Version</th>
            <th className="py-1 font-normal">Arm</th>
            <th className="py-1 text-right font-normal">D1</th>
            {cols.map((c) => (
              <th key={c.key} className="py-1 text-right font-normal">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {versions.flatMap((v) =>
            v.arms.map((a, i) => (
              <tr key={`${v.version}|${a.variant}`} className="border-t border-slate-800 text-slate-300">
                <td className="py-1">{i === 0 ? v.version : ''}</td>
                <td className="py-1 text-slate-400">{NEXT_SHIFT_ARM_LABEL[a.variant]}</td>
                <td className="py-1 text-right tabular-nums">
                  <div>{a.pct != null ? `${a.pct}%` : 'building'}</div>
                  <div className="text-[10px] text-slate-500">
                    {`${a.retained.toLocaleString()} of ${a.cohort.toLocaleString()}`}
                  </div>
                </td>
                {/* Control never sees the card, so its card columns stay blank. */}
                {cols.map((c) => (
                  <td key={c.key} className="py-1 text-right tabular-nums">
                    {a.variant === 'control' ? '' : (a[c.key] ?? 0).toLocaleString()}
                  </td>
                ))}
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  )
}

function DebriefTable({ rows }: { rows: PostLevelActionRow[] }) {
  const cells: { key: string; audience: string; isAcademy: boolean; byAction: Partial<Record<PostLevelAction, PostLevelActionRow>> }[] = []
  for (const r of rows) {
    const key = `${r.audience}|${r.isAcademy ? 'academy' : 'real'}`
    let cell = cells.find((c) => c.key === key)
    if (!cell) {
      cell = { key, audience: r.audience, isAcademy: r.isAcademy, byAction: {} }
      cells.push(cell)
    }
    cell.byAction[r.action] = r
  }
  if (cells.length === 0) {
    return <p className="text-xs text-slate-400">No post-level actions in the window.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-400">
            <th className="py-1 font-normal">Audience</th>
            <th className="py-1 font-normal">Level</th>
            {POST_LEVEL_ACTION_COLUMNS.map((c) => (
              <th key={c.key} className="py-1 text-right font-normal">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cells.map((cell) => (
            <tr key={cell.key} className="border-t border-slate-800 text-slate-300">
              <td className="py-1">{POST_LEVEL_AUDIENCE_LABEL[cell.audience] ?? cell.audience}</td>
              <td className="py-1 text-slate-400">{cell.isAcademy ? 'Academy' : 'Real'}</td>
              {POST_LEVEL_ACTION_COLUMNS.map((c) => {
                const r = cell.byAction[c.key]
                return (
                  <td key={c.key} className="py-1 text-right tabular-nums">
                    <div>{(r?.count ?? 0).toLocaleString()}</div>
                    <div className="text-[10px] text-slate-500">
                      {r?.pctOfShown != null ? `${r.pctOfShown}%` : ' '}
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PendingCard({ title, note }: { title: string; note: string }) {
  return (
    <div className="rounded-lg bg-slate-900/60 p-4 ring-1 ring-white/5">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-amber-400/80" />
        <h2 className="text-sm font-semibold text-slate-300">{title}</h2>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Populating — {note}</p>
    </div>
  )
}
